import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Button, Checkbox } from '@zep-us/zus';
import { ExpandIcon } from '@zep-us/zus-icons';
import { LoaderCircle } from 'lucide-react';
import { type PdfPreviewDocument, type PdfThumbnail } from '@/shared/utils/pdfPreviewAdapter';
import { cn } from '@/utils/tailwind.util';
import { type PdfThumbnailQueue } from '../utils/pdfThumbnailQueue';
type AiBuilderPdfPageCardProps = {
  pageNumber: number;
  preview: PdfPreviewDocument;
  queue: PdfThumbnailQueue;
  selected: boolean;
  detailLoading?: boolean;
  onError: () => void;
  onOpenDetail: (imageSrc: string) => void;
  onToggle: () => void;
  openDetailLabel: string;
  openDetailText: string;
  previewLoadingLabel: string;
  toggleSelectionLabel: string;
  className?: string;
};
const isCancelledError = (error: unknown) => error instanceof DOMException && error.name === 'AbortError' || typeof error === 'object' && error !== null && 'code' in error && error.code === 'cancelled';
export const AiBuilderPdfPageCard = ({
  className,
  detailLoading = false,
  onError,
  onOpenDetail,
  onToggle,
  openDetailLabel,
  openDetailText,
  pageNumber,
  preview,
  previewLoadingLabel,
  queue,
  selected,
  toggleSelectionLabel
}: AiBuilderPdfPageCardProps) => {
  const {
    ref,
    inView
  } = useInView({
    rootMargin: '400px 0px',
    triggerOnce: true
  });
  const [thumbnail, setThumbnail] = useState<PdfThumbnail | null>(null);
  useEffect(() => {
    if (!inView || thumbnail) return;
    const controller = new AbortController();
    let active = true;
    void queue.run(async () => {
      const render = () => preview.renderThumbnail(pageNumber, {
        maxWidth: 220,
        signal: controller.signal
      });
      try {
        return await render();
      } catch (error) {
        if (isCancelledError(error)) throw error;
        return render();
      }
    }, controller.signal).then(nextThumbnail => {
      if (active) setThumbnail(nextThumbnail);
    }).catch(error => {
      if (active && !isCancelledError(error)) {
        onError();
      }
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [inView, onError, pageNumber, preview, queue, thumbnail]);
  return <article className={cn('group/card relative flex w-full flex-col overflow-hidden rounded-sm text-body-md-semibold transition-[border-color,background-color,box-shadow] hover:shadow-sm', selected ? 'border-2 border-strong bg-strong-alternative' : 'border border-alternative bg-default hover:border-assistive', className)} data-sentry-component="AiBuilderPdfPageCard" data-sentry-source-file="AiBuilderPdfPageCard.tsx">
      <button type="button" role="checkbox" aria-checked={selected} aria-label={toggleSelectionLabel} data-slot="page-selection-control" className="absolute inset-0 z-[1] cursor-pointer rounded-sm bg-transparent focus-visible:outline-none focus-visible:focus-ring" onClick={onToggle} />

      <div ref={ref} className="flex min-h-0 flex-1 flex-col overflow-hidden px-sm pt-sm">
        <div data-slot="page-preview" className="group/preview relative z-[2] flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-xs">
          <button type="button" tabIndex={-1} aria-hidden data-slot="page-preview-selection-control" className="absolute inset-0 z-[1] cursor-pointer bg-transparent" onClick={onToggle} />

          {thumbnail ?
        // PDF.js가 로컬 파일에서 만든 data URL이며 외부 image loader 대상이 아니다.
        <img src={thumbnail.dataUrl} alt="" className="max-h-full max-w-full rounded-2xs object-contain" /> : <LoaderCircle aria-label={previewLoadingLabel} className="size-5 animate-spin text-neutral" />}

          {thumbnail && <div data-slot="preview-overlay" className="pointer-events-none absolute inset-0 z-[2] hidden items-center justify-center bg-dimmed opacity-0 transition-opacity duration-150 group-focus-within/preview:opacity-100 group-hover/preview:opacity-100 tablet-sm:flex">
              <Button type="button" aria-label={openDetailLabel} variant="contained" intent="neutral" size="sm" rounded="full" leftSlot={detailLoading ? <LoaderCircle aria-hidden className="size-4 animate-spin" /> : <ExpandIcon size="xs" />} disabled={detailLoading} aria-busy={detailLoading} className="pointer-events-auto" onClick={() => onOpenDetail(thumbnail.dataUrl)}>
                {openDetailText}
              </Button>
            </div>}
        </div>
      </div>

      <div className="flex shrink-0 items-end justify-between pb-sm pl-md pr-sm pt-xs">
        <span>{pageNumber}</span>
        <Checkbox checked={selected} tabIndex={-1} aria-hidden className="pointer-events-none" data-sentry-element="Checkbox" data-sentry-source-file="AiBuilderPdfPageCard.tsx" />
      </div>
    </article>;
};